import { DutyGroup } from "@/constants/importCharges";

/**
 * Maps a product onto the commodity bucket that decides its import duty rate.
 *
 * Keyed on the ROOT category slug, because that is the only level stable enough
 * to reason about: leaf categories are added and renamed constantly, and a
 * mapping that has to know about "bucket-tassen" will silently rot. The root
 * set has been the same eight for years.
 *
 * `tassen` matters most. Handbags are HS 4202 and carry 3% UK duty, unlike
 * furniture and art which are duty-free, so second-hand bags are the one part
 * of the catalogue where duty is not a rounding error.
 */
const ROOT_SLUG_TO_DUTY_GROUP: Record<string, DutyGroup> = {
  tassen: "leatherGoods",
  kunst: "art",
  meubels: "furnishings",
  verlichting: "furnishings",
  decoratie: "furnishings",
  tafelwaar: "furnishings",
  cadeaus: "furnishings",
  onderhoudsproducten: "furnishings",
};

/**
 * Falls back to `furnishings`, which is the 0%-duty bucket. That is the right
 * way to be wrong: a new root category is far more likely to be another kind of
 * homeware than another kind of leather, and under-quoting duty by three points
 * is a smaller error than inventing duty on a lamp.
 */
export const DEFAULT_DUTY_GROUP: DutyGroup = "furnishings";

type CategoryLike = { slug?: string | null } | null | undefined;

/**
 * `breadcrumbs` runs root-first, so the first entry is the root category. We
 * accept the raw list rather than a resolved root so callers can pass whichever
 * of `breadcrumbs` or `categories` they already have on the product.
 */
export const resolveDutyGroup = (
  breadcrumbs?: readonly CategoryLike[] | null
): DutyGroup => {
  if (!breadcrumbs?.length) {
    return DEFAULT_DUTY_GROUP;
  }

  for (const crumb of breadcrumbs) {
    const slug = crumb?.slug?.toLowerCase();
    if (slug && ROOT_SLUG_TO_DUTY_GROUP[slug]) {
      return ROOT_SLUG_TO_DUTY_GROUP[slug];
    }
  }

  return DEFAULT_DUTY_GROUP;
};
